import { EditOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { Dropdown, Menu, Typography } from "antd";
import ListTable from "common/ListTable";
import { IMPORT_LC_TRACKER } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import {
  IMPORT_LC_EXPENSE_TRACKER_CREATE_PERMISSION,
  IMPORT_LC_EXPENSE_TRACKER_EDIT_PERMISSION,
  IMPORT_LC_EXPENSE_TRACKER_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";

const ImportLcExpense = (props) => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [permitList, setPermitList] = useState(null);
  const [totalData, setTotalData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Route
  const navigate = useNavigate();

  // Antd Constants
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  const columns = [
    {
      title: "Sales Contract",
      dataIndex: ["sales_contract", "reference_no"],
      key: "reference_no",
      render: (text, record) => record.sales_contract?.reference_no || "-",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer_info", "name"],
      key: "buyer_id",
      render: (text, record) => record.buyer_info?.name || "-",
    },
    {
      title: "BTB LC No",
      dataIndex: "btb_lc_no",
      key: "btb_lc_no",
    },
    {
      title: "Supplier",
      dataIndex: ["supplier", "name"],
      key: "supplier_id",
      render: (text, record) => record.supplier?.name || "-",
    },
    {
      title: "LC Value",
      dataIndex: "lc_value",
      key: "lc_value",
      render: (text) => text?.toFixed(2) || "-",
    },
    {
      title: "Amendment Charges",
      dataIndex: "amendment_charges",
      key: "amendment_charges",
      render: (text) => text?.toFixed(2) || "-",
    },
    {
      title: "LC Stamp Charges",
      dataIndex: "lc_stamp_charges",
      key: "lc_stamp_charges",
      render: (text) => text?.toFixed(2) || "-",
    },
    {
      title: "Issue Commission",
      dataIndex: "issue_commission",
      key: "issue_commission",
      render: (text) => text?.toFixed(2) || "-",
    },
    {
      title: "Swift Charges",
      dataIndex: "swift_charges",
      key: "swift_charges",
      render: (text) => text?.toFixed(2) || "-",
    },
    // {
    //   title: "Total",
    //   key: "total",
    //   render: (_, record) => {
    //     const total = (
    //       (record.lc_value || 0) +
    //       (record.amendment_charges || 0) +
    //       (record.lc_stamp_charges || 0) +
    //       (record.issue_commission || 0) +
    //       (record.swift_charges || 0)
    //     ).toFixed(2);
    //     return total || "-";
    //   },
    // },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  // Function to get styles list
  const getAllPermitList = useCallback(
    async (passedObject) => {
      const { page: requestedPage, ...requestFilter } = passedObject || {};
      const pageNumber = requestedPage != null ? requestedPage : currentPage;
      if (requestedPage != null && Number(requestedPage) !== Number(currentPage)) {
        setCurrentPage(Number(requestedPage));
      }
      setFetchError(null);
      const customQuery = `${IMPORT_LC_TRACKER}/?page=${pageNumber}`;
      let res = await getData(customQuery, false, requestFilter);

      if (res) {
        let masterData = res?.data;
        setPermitList(masterData?.data?.data);
        setTotalData(masterData?.data?.total);
        setRangeData(masterData?.data?.per_page);
      } else {
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [currentPage],
  );

  useEffect(() => {
    setLoading(true);
    getAllPermitList(filterData);
  }, [currentPage, filterData, getAllPermitList]);

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link
              to={`/import-export-expense-tracking/import-lc-expense/update/${data.id}`}
            >
              <EditOutlined /> Edit
            </Link>
          ),
          key: "0",
          disabled: !hasPermission([IMPORT_LC_EXPENSE_TRACKER_EDIT_PERMISSION]),
        },
        {
          label: (
            <Link
              to={`/import-export-expense-tracking/import-lc-expense/view/${data.id}`}
            >
              <EyeOutlined /> View
            </Link>
          ),
          key: "1",
          disabled: !hasPermission([IMPORT_LC_EXPENSE_TRACKER_VIEW_PERMISSION]),
        },
      ]}
    />
  );

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const onAdd = () => {
    navigate("/import-export-expense-tracking/import-lc-expense/create");
  };

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={permitList}
        filterAPI={getAllPermitList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="style"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                // refresh={refresh}
                // setRefresh={setRefresh}
                handleAddItemOpen={onAdd}
                disabled={
                  !hasPermission([IMPORT_LC_EXPENSE_TRACKER_CREATE_PERMISSION])
                }
                addButtonText={
                  hasPermission([IMPORT_LC_EXPENSE_TRACKER_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                hideFilterButton
              />
            }
            title={<Title level={5}>Import LC Expense</Title>}
          />
        }
        content={
          <>
            {/* {memoizedCustomFilter} */}
            <div className="table" style={{ marginTop: 10, padding: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllPermitList(filterData)}
                emptyText="No records to show yet."
                dataSource={permitList}
                columns={columns}
                scroll={{
                  x: "max-content",
                }}
                pagination={{
                  showSizeChanger: false,
                  current: currentPage,
                  total: totalData,
                  pageSize: rangeData,
                  onChange: (page) => paginate(page),
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default ImportLcExpense;
